import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';

import * as actions from '../../../store/actions/index';
import { storageEmpresaNombre } from '../../../assets/shared/sessionData';
import { nombreComprobante, terceroComprobante } from '../textosComprobante';
import {
  PAISES_TELEFONO,
  PAIS_POR_DEFECTO,
  paisPorCodigo,
  separarTelefono,
  armarTelefono,
  telefonoValido
} from '../../../assets/shared/paisesTelefono';
import { botonWhatsapp } from '../coloresWhatsapp';

const useStyles = makeStyles((theme) => ({
  campo: {
    width: '100%',
    marginBottom: '15px'
  },
  prefijo: {
    color: theme.palette.text.secondary
  },
  botonEnviar: botonWhatsapp,
  vistaPrevia: {
    padding: '10px',
    backgroundColor: '#dcf8c6',
    borderRadius: '6px',
    whiteSpace: 'pre-wrap'
  }
}));

/**
 * Envío del PDF de un comprobante por WhatsApp, con la cuenta de WhatsApp Business de la empresa.
 * El teléfono se precarga con el del cliente en el Eiffel y se puede cambiar. El prefijo del país (Argentina,
 * +54 9, por defecto) se elige de la lista y el usuario escribe sólo el área y el número. El texto es el del
 * template aprobado por Meta, así que no se puede editar.
 */
const ImpresionWhatsappModal = (props) => {
  const { comprobante, onClose, onLoadWhatsappDestinatario, onLoadRemitente, onEnviarWhatsapp } = props;
  const classes = useStyles();
  const [pais, setPais] = useState(PAIS_POR_DEFECTO);
  const [telefono, setTelefono] = useState('');
  // Quién firma el mensaje (Clientes.Nombre); mientras carga, el de la empresa de la sesión.
  const [remitente, setRemitente] = useState('');
  const [configurado, setConfigurado] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  // En celular el modal ocupa toda la pantalla.
  const pantallaCompleta = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  useEffect(() => {
    if (!comprobante) return undefined;

    let cancelado = false;
    setPais(PAIS_POR_DEFECTO);
    setTelefono('');
    setRemitente((storageEmpresaNombre() || '').trim());
    onLoadRemitente((nombre) => !cancelado && setRemitente(nombre));
    setConfigurado(true);
    setEnviando(false);
    setCargando(true);
    onLoadWhatsappDestinatario(
      comprobante.idCliente,
      (destinatario) => {
        if (cancelado) return;
        setConfigurado(destinatario.configurado);
        const separado = separarTelefono(destinatario.telefono, destinatario.normalizado);
        setPais(separado.pais);
        setTelefono(separado.numero);
        setCargando(false);
      },
      () => !cancelado && setCargando(false),
      comprobante.idSistema
    );

    return () => {
      cancelado = true;
    };
  }, [comprobante, onLoadWhatsappDestinatario, onLoadRemitente]);

  const nombreCliente = comprobante && comprobante.nombre ? comprobante.nombre.trim() : terceroComprobante(comprobante);
  const puedeEnviar = !enviando && !cargando && configurado && telefonoValido(pais, telefono);

  const argentino = pais.codigo === PAIS_POR_DEFECTO.codigo;
  const ayudaTelefono = cargando
    ? `Buscando el teléfono del ${terceroComprobante(comprobante)}...`
    : telefono.trim() === ''
      ? `El ${terceroComprobante(comprobante)} no tiene teléfono cargado en el Eiffel. Ingrese el celular con código de área.`
      : !telefonoValido(pais, telefono)
        ? argentino
          ? 'Ingrese el celular con código de área, sin 0 ni 15, por ejemplo 351 1234567.'
          : 'Ingrese el celular completo, sin el código del país.'
        : 'Celular con código de área. Puede cambiarlo.';

  const enviar = () => {
    setEnviando(true);
    onEnviarWhatsapp(
      comprobante,
      { telefono: armarTelefono(pais, telefono), nombreCliente, comprobante: nombreComprobante(comprobante) },
      () => onClose(),
      () => setEnviando(false)
    );
  };

  return (
    <Dialog open={!!comprobante} onClose={enviando ? undefined : onClose} fullWidth fullScreen={pantallaCompleta} maxWidth="sm">
      <DialogTitle>{comprobante ? `Enviar por WhatsApp ${nombreComprobante(comprobante)}` : ''}</DialogTitle>
      <DialogContent dividers>
        {!cargando && !configurado ? (
          <Typography>
            El envío por WhatsApp no está configurado para esta empresa. Comuníquese con Gauss para darlo de alta.
          </Typography>
        ) : (
          <React.Fragment>
            <TextField
              className={classes.campo}
              label="Celular"
              value={telefono}
              onChange={(event) => setTelefono(event.target.value)}
              placeholder={argentino ? '351 1234567' : ''}
              error={!cargando && !telefonoValido(pais, telefono)}
              helperText={ayudaTelefono}
              disabled={enviando}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Select
                      value={pais.codigo}
                      onChange={(event) => setPais(paisPorCodigo(event.target.value))}
                      renderValue={(codigo) => paisPorCodigo(codigo).etiqueta}
                      disableUnderline
                      disabled={enviando}
                      className={classes.prefijo}
                      MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                    >
                      {PAISES_TELEFONO.map((p) => (
                        <MenuItem key={p.codigo} value={p.codigo}>
                          {`${p.nombre} (${p.etiqueta})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
                endAdornment: cargando ? <CircularProgress size={16} /> : null
              }}
            />
            {comprobante && (
              <Typography variant="body2" className={classes.vistaPrevia}>
                {`📄 ${nombreComprobante(comprobante)}.pdf\n\nHola ${nombreCliente}, te enviamos adjunto el comprobante ${nombreComprobante(comprobante)} de ${remitente || 'la empresa'}. Ante cualquier consulta, respondé este mensaje.`}
              </Typography>
            )}
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          className={classes.botonEnviar}
          startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon />}
          disabled={!puedeEnviar}
          onClick={enviar}
        >
          {enviando ? 'Enviando...' : 'Enviar'}
        </Button>
        <Button onClick={onClose} disabled={enviando}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    onLoadWhatsappDestinatario: (idCliente, success, fail, idSistema) =>
      dispatch(actions.loadWhatsappDestinatarioImpresion(idCliente, success, fail, idSistema)),
    onLoadRemitente: (success, fail) => dispatch(actions.loadRemitenteImpresion(success, fail)),
    onEnviarWhatsapp: (comprobante, whatsapp, success, fail) =>
      dispatch(actions.enviarWhatsappComprobanteImpresion(comprobante, whatsapp, success, fail))
  };
};

export default connect(null, mapDispatchToProps)(ImpresionWhatsappModal);
